import { createParser } from 'eventsource-parser';

export async function streamOpenAIResponse(
  messages: { role: 'user' | 'assistant'; content: string }[],
  onChunk: (chunk: string) => void,
  onError: (error: Error) => void
) {
  try {
    // Get the OpenAI API key from environment variables
    const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key is missing. Please check your environment variables.');
    }
    
    // Log the request for debugging (remove in production)
    console.log('Making OpenAI API request with messages:', 
      messages.map(m => ({ role: m.role, contentLength: m.content.length }))
    );
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `You are an AI content creation assistant, specialized in helping creators optimize their social media presence and content strategy. Your expertise includes:

1. Content Creation
- Brainstorming content ideas
- Writing and editing posts
- Optimizing content for different platforms
- Creating engaging headlines and captions

2. Social Media Strategy
- Platform-specific best practices
- Audience engagement techniques
- Hashtag strategies
- Posting schedules and timing

3. Analytics & Performance
- Content performance analysis
- Engagement metrics interpretation
- Growth strategies
- A/B testing recommendations

4. Industry Trends
- Current social media trends
- Platform updates and features
- Content format recommendations
- Emerging opportunities

Be concise, practical, and always provide actionable advice. When appropriate, format your responses using markdown for better readability.`
          },
          ...messages
        ],
        stream: true,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('OpenAI API error:', response.status, errorText);
      throw new Error(`OpenAI API error: ${response.status} ${response.statusText}. Details: ${errorText}`);
    }

    const parser = createParser((event) => {
      if (event.type === 'event') {
        if (event.data === '[DONE]') return;
        try {
          const json = JSON.parse(event.data);
          const content = json.choices[0]?.delta?.content;
          if (content) onChunk(content);
        } catch (e) {
          console.error('Error parsing SSE message:', e);
        }
      }
    });

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      parser.feed(chunk);
    }
  } catch (error) {
    console.error('OpenAI streaming error:', error);
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
}